/**
 * Phase 25 — Plan Editor URL parser.
 *
 * Covers: PLAN25-01 (mode resolution from pathname), PLAN25-02 (?spaceId=N
 * pre-scope from search string). Pure — no wouter / window dependency so
 * tests run without a DOM.
 *
 * Plan 25-02 replaced the Wave 0 stub with real logic.
 */

export interface ParsedPlanEditorUrl {
  mode: 'create' | 'edit';
  planId: number | null;
  spaceId: number | null;
}

export function parsePlanEditorUrl(pathname: string, search: string): ParsedPlanEditorUrl {
  // mode + planId from pathname.
  // Expected shapes:
  //   /billing/plans/new       → create
  //   /billing/plans/:id       → edit
  //   anything else            → treated as create with planId=null (caller never hits this when wouter routing is correct)
  const newMatch = /^\/billing\/plans\/new(?:\/.*)?$/.exec(pathname);
  if (newMatch) {
    return { mode: 'create', planId: null, spaceId: extractSpaceId(search) };
  }
  const idMatch = /^\/billing\/plans\/([^/]+)/.exec(pathname);
  if (idMatch) {
    const raw = Number(idMatch[1]);
    const planId = Number.isFinite(raw) && raw > 0 ? raw : null;
    return { mode: 'edit', planId, spaceId: extractSpaceId(search) };
  }
  // Fallback — caller should never hit this if wouter routing is correct.
  return { mode: 'create', planId: null, spaceId: extractSpaceId(search) };
}

function extractSpaceId(search: string): number | null {
  if (!search) return null;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const raw = params.get('spaceId');
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}
